import styled from 'styled-components'

export const PageRoot = styled.main`
  width: 100%;
  min-height: 100vh;
  padding: 112px clamp(18px, 4vw, 56px) 64px;
  background:
    radial-gradient(circle at 50% 0%, ${p => p.theme.palette.colors.shellGradientStart} 0%, ${p => p.theme.palette.colors.shellGradientEnd} 42%),
    ${p => p.theme.palette.colors.background};

  @media (max-width: 991px) {
    padding: 96px 18px 48px;
  }

  @media (max-width: 480px) {
    padding: 88px 14px 36px;
  }
`

export const IntroPanel = styled.section`
  width: min(620px, 100%);
  min-height: calc(100svh - 220px);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 22px;
  text-align: center;
  animation: surveyReveal 0.34s ease both;

  @keyframes surveyReveal {
    from {
      opacity: 0;
      transform: translateY(12px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`

export const IntroBadge = styled.span`
  display: inline-flex;
  min-height: 28px;
  align-items: center;
  padding: 5px 12px;
  border: 1px solid ${p => p.theme.palette.colors.borderStrong};
  border-radius: 999px;
  background: ${p => p.theme.palette.colors.badgeMuted};
  color: ${p => p.theme.palette.blue.main};
  font-family: Poppins;
  font-size: 12px;
  font-weight: 600;
`

export const IntroTitle = styled.h1`
  max-width: 580px;
  margin: 4px 0 0;
  color: ${p => p.theme.palette.colors.text};
  font-family: Gilroy;
  font-size: clamp(38px, 6vw, 56px);
  font-weight: 700;
  line-height: 1.02;
  letter-spacing: -0.04em;
`

export const IntroDescription = styled.p`
  max-width: 560px;
  margin: 0;
  color: ${p => p.theme.palette.colors.muted};
  font-family: Lato;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.65;
`

export const IntroMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 22px;
  margin: 8px 0 4px;
  color: ${p => p.theme.palette.colors.subdued};

  @media (max-width: 520px) {
    align-items: flex-start;
    flex-direction: column;
    gap: 9px;
  }
`

export const IntroMetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: Lato;
  font-size: 13px;
  font-weight: 500;

  &::before {
    content: '';
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: ${p => p.theme.palette.blue.main};
  }
`

export const SurveyWorkspace = styled.section`
  width: min(760px, 100%);
  margin: 0 auto;
  animation: surveyStepReveal 0.3s ease both;

  @keyframes surveyStepReveal {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`

export const ProgressHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
`

export const ProgressSteps = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  overflow-x: auto;
  padding: 4px 3px 8px;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`

export const StepChip = styled.span`
  width: 36px;
  height: 28px;
  flex: 0 0 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${p => p.active ? p.theme.palette.blue.main : p.theme.palette.colors.borderSoft};
  border-radius: 999px;
  background: ${p => p.active || p.completed ? p.theme.palette.colors.badgeMuted : 'transparent'};
  color: ${p => p.active || p.completed ? p.theme.palette.blue.main : p.theme.palette.colors.subdued};
  font-family: Poppins;
  font-size: 11px;
  font-weight: 700;
`

export const ProgressLabel = styled.span`
  flex: 0 0 auto;
  color: ${p => p.theme.palette.colors.subdued};
  font-family: Poppins;
  font-size: 12px;
  font-weight: 600;
`

export const ProgressBar = styled.div`
  width: 100%;
  height: 3px;
  margin-top: 9px;
  overflow: hidden;
  border-radius: 999px;
  background: ${p => p.theme.palette.colors.buttonGhost};
`

export const ProgressFill = styled.div`
  width: ${p => `${p.progress}%`};
  height: 100%;
  border-radius: inherit;
  background: ${p => p.theme.palette.blue.main};
  transition: width 0.32s ease;
`

export const SurveyHeader = styled.header`
  padding: 42px 0 30px;
  border-bottom: 1px solid ${p => p.theme.palette.colors.borderSoft};
`

export const SurveyMeta = styled.span`
  color: ${p => p.theme.palette.colors.subdued};
  font-family: Poppins;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`

export const SurveyTitle = styled.h1`
  margin: 8px 0 0;
  color: ${p => p.theme.palette.colors.text};
  font-family: Gilroy;
  font-size: clamp(30px, 5vw, 42px);
  font-weight: 700;
  line-height: 1.08;
  letter-spacing: -0.035em;
`

export const SurveyDescription = styled.p`
  max-width: 640px;
  margin: 12px 0 0;
  color: ${p => p.theme.palette.colors.muted};
  font-family: Lato;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.6;
`

export const QuestionSection = styled.div`
  padding: 38px 0 42px;
  border-bottom: 1px solid ${p => p.theme.palette.colors.borderSoft};
  animation: questionReveal 0.28s ease both;

  @keyframes questionReveal {
    from {
      opacity: 0;
      transform: translateX(8px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
`

export const Navigation = styled.footer`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-top: 28px;

  @media (max-width: 420px) {
    align-items: stretch;
    flex-direction: column-reverse;

    & > * {
      width: 100%;
    }
  }
`

export const StatePanel = styled.section`
  width: min(720px, 100%);
  min-height: calc(100svh - 220px);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 18px;
  text-align: center;
`

export const CompletionMark = styled.div`
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${p => p.completed ? p.theme.palette.blue.main : p.theme.palette.colors.borderStrong};
  border-radius: 50%;
  background: ${p => p.completed ? p.theme.palette.colors.badgeMuted : p.theme.palette.colors.buttonGhost};
  color: ${p => p.completed ? p.theme.palette.blue.main : p.theme.palette.colors.muted};
  font-family: Poppins;
  font-size: 26px;
  font-weight: 800;
`

export const StateTitle = styled.h1`
  margin: 0;
  color: ${p => p.theme.palette.colors.text};
  font-family: Gilroy;
  font-size: clamp(32px, 5vw, 44px);
  font-weight: 700;
  line-height: 1.1;
`

export const StateDescription = styled.p`
  max-width: 580px;
  margin: 0;
  color: ${p => p.theme.palette.colors.muted};
  font-family: Lato;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.65;
`
